// Zod schemas used for runtime validation in both worker (request bodies)
// and web (form validation). Pair each schema with a TS type via z.infer.

import { z } from 'zod';
import { CANONICAL_UOMS } from './uom.js';

// === Enums ===

export const appRoleSchema = z.enum(['back_office', 'owner', 'admin']);

export const customerTypeSchema = z.enum(['residential', 'builder', 'commercial']);

export const jobStatusSchema = z.enum([
  'created',
  'estimated',
  'quoted',
  'accepted',
  'scheduled',
  'installed',
  'invoiced',
  'completed',
  'disqualified',
]);

export const jobTypeSchema = z.enum(['new_work', 'repair', 'warranty']);
export const productLineSchema = z.enum(['gutter', 'insulation']);

export const trackingLevelSchema = z.enum(['rigorous', 'loose']);

export const skuUnitSchema = z.enum(['ft', 'ea', 'box', 'bag', 'tube', 'can']);

export const inventoryTxnReasonSchema = z.enum([
  'receipt',
  'pull',
  'return',
  'damage',
  'count_correction',
]);

// === Customer ===

export const customerCreateSchema = z.object({
  type: customerTypeSchema,
  // For residential: `name` is required (the human's name). For
  // builder/commercial: companyName is the canonical org name; clients
  // may also send `name` for back-compat — server resolves the
  // canonical display string from companyName ?? contactName ?? name.
  name: z.string().min(1).max(200).optional(),
  companyName: z.string().max(200).nullable().optional(),
  contactName: z.string().max(200).nullable().optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().max(50).nullable().optional(),
  discountPct: z.number().int().min(0).max(100).default(0),
  notes: z.string().nullable().optional(),
});
export type CustomerCreateInput = z.infer<typeof customerCreateSchema>;

export const customerUpdateSchema = customerCreateSchema.partial();
export type CustomerUpdateInput = z.infer<typeof customerUpdateSchema>;

// === Job Templates ===

export const jobTemplateCreateSchema = z.object({
  name: z.string().min(1).max(200),
  fixedPriceCents: z.number().int().nonnegative().default(0),
  displayOrder: z.number().int().optional(),
  active: z.boolean().default(true),
});
export type JobTemplateCreateInput = z.infer<typeof jobTemplateCreateSchema>;

// === Job ===

export const jobCreateSchema = z.object({
  customerId: z.string().uuid(),
  displayName: z.string().min(1).max(200),
  poNumber: z.string().max(100).nullable().optional(),
  address: z.string().min(1).max(500),
  jobType: jobTypeSchema.default('new_work'),
  /** Trade bucket. The New Job form forces an explicit choice in the
   *  UI; the API defaults to 'gutter' for back-compat with test
   *  scripts and any direct API caller that hasn't been updated.
   *  Drives the Gutter / Insulation tabs on the Jobs page. */
  productLine: productLineSchema.default('gutter'),
  targetInstallDate: z.string().nullable().optional(),
  /**
   * If set, create this job from a manufactured-home template. The
   * server pins quote_amount_cents to the template's fixed_price_cents.
   * Job starts at status='created' with no BoM — office fills line
   * items via the PricingCalculator or the crew via /estimate.
   */
  templateId: z.string().uuid().optional(),
  /**
   * Legacy field — accepted for backwards compat but no longer used.
   * Templates no longer carry a BoM, so there's nothing to color-swap.
   */
  templateColor: z.string().min(1).max(100).optional(),
});
export type JobCreateInput = z.infer<typeof jobCreateSchema>;

export const jobUpdateSchema = jobCreateSchema.partial().extend({
  status: jobStatusSchema.optional(),
  // Free-text notes are PATCH-only — not set at job creation. Null
  // clears the field; undefined leaves it alone.
  notes: z.string().nullable().optional(),
  // Date the invoice was sent to the customer, captured by the modal
  // that fires on installed→invoiced transitions. Optional — if the
  // PATCH sets status='invoiced' without providing invoicedAt, the
  // worker falls back to NOW() as a reasonable default.
  invoicedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD').nullable().optional(),
  // External (QuickBooks) invoice number the office types in on the
  // installed→invoiced transition (gutter: one per job). The app no
  // longer generates invoices; it captures the QBO number. Null clears,
  // undefined leaves alone. Persisted to jobs.external_invoice_number.
  invoiceNumber: z.string().max(64).nullable().optional(),
  // Office override of the line-item-computed total. Editable inline
  // on the Quote card while the customer is still negotiating
  // (status in {created, estimated, quoted}). Edits made in 'quoted'
  // state auto-revert the job to 'estimated' so the office is forced
  // to re-send a fresh quote before the customer accepts. Worker locks
  // edits once status >= accepted (commitment point — line items are
  // now what the install will be built against).
  quoteAmountCents: z.number().int().nonnegative().nullable().optional(),
  // Customer labor footage override (migration 0054): gutter manual LF
  // (null = derive from ft estimate lines) / insulation hand-entered SF
  // (null = none). Same edit window + quoted-auto-revert as quoteAmountCents.
  // numeric(12,2) → allow up to 2 decimals; null clears, undefined leaves alone.
  quoteLaborFootageOverride: z.number().nonnegative().nullable().optional(),
  // Per-job labor rate override (migration 0054), integer cents per unit. null
  // clears → tenant default. Same edit window + quoted-auto-revert as the amount.
  quoteLaborRateCentsPerUnitOverride: z.number().int().nonnegative().nullable().optional(),
  // Single per-job ACTUAL installed footage (migration 0056): LF gutter /
  // SF insulation by product_line. Last-write-wins (office authoritative).
  // No edit-window gate — capture is post-hoc, valid in any status. null
  // clears, undefined leaves alone. numeric(12,2) → allow up to 2 decimals.
  actualInstalledFootage: z.number().nonnegative().nullable().optional(),
});
export type JobUpdateInput = z.infer<typeof jobUpdateSchema>;

// === SKU Category ===

export const skuCategoryCreateSchema = z.object({
  name: z.string().min(1).max(100),
  defaultMarkupPct: z.number().min(0).max(1000).default(0),
  displayOrder: z.number().int().min(0).default(0),
});
export type SkuCategoryCreateInput = z.infer<typeof skuCategoryCreateSchema>;

// === SKU ===

export const skuCreateSchema = z.object({
  categoryId: z.string().uuid(),
  code: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  variant: z.string().max(100).nullable().optional(),
  unit: skuUnitSchema,
  costCents: z.number().int().min(0),
  priceCentsOverride: z.number().int().min(0).nullable().optional(),
  onHand: z.number().min(0).default(0),
  reorderThreshold: z.number().min(0).default(0),
  trackingLevel: trackingLevelSchema.default('rigorous'),
  isCommon: z.boolean().optional(),
  // Parent/child SKUs (migration 0069). isParent: an abstract coil profile that is
  // never selectable/consumable. parentSkuId: child → parent link.
  isParent: z.boolean().optional(),
  parentSkuId: z.string().uuid().nullable().optional(),
  // SF/unit coverage (migration 0060); NULL for non-batting SKUs.
  coverageSf: z.number().min(0).nullable().optional(),
  // Insulation batting attributes (migration 0068). Display-only this round;
  // accepted on create/update so the contract is stable (not yet UI-editable).
  rValue: z.string().max(20).nullable().optional(),
  face: z.string().max(10).nullable().optional(),
  widthIn: z.number().min(0).nullable().optional(),
  lengthIn: z.number().min(0).nullable().optional(),
  // Category-specific spec columns (migrations 0071/0073), all optional + nullable;
  // captured by the category-aware New SKU dialog. Generic columns shared across
  // categories (the form shows only the selected category's subset). All text except
  // lbsPerLf (numeric); widthIn/lengthIn/coverageSf above are the other numerics.
  material: z.string().max(64).nullable().optional(),
  color: z.string().max(64).nullable().optional(),
  thickness: z.string().max(64).nullable().optional(),
  flatCoilWidth: z.string().max(64).nullable().optional(),
  lbsPerLf: z.number().min(0).nullable().optional(),
  coilProfile: z.string().max(64).nullable().optional(),
  gutterFaceSize: z.string().max(64).nullable().optional(),
  coilTemper: z.string().max(64).nullable().optional(),
  style: z.string().max(64).nullable().optional(),
  size: z.string().max(64).nullable().optional(),
  angle: z.string().max(64).nullable().optional(),
  typeAttr: z.string().max(64).nullable().optional(),
  lengthAttr: z.string().max(64).nullable().optional(),
  widthAttr: z.string().max(64).nullable().optional(),
  brand: z.string().max(64).nullable().optional(),
  // Service Partners 12-digit Part No (migration 0073). Unique per tenant when set.
  partNo: z.string().max(64).nullable().optional(),
  // UoM / conversion (migration 0071). conversionFactor is editable in the
  // Inventory detail Operational tab; saving it sets conversionFactorSource to
  // 'override' so the master CSV import won't clobber the manual value. USED in
  // pricing: cost ÷ conversionFactor → price per consumptionUom (computeSellPriceCents).
  conversionFactor: z.number().min(0).nullable().optional(),
  conversionFactorSource: z.enum(['import', 'coil_computed', 'override']).optional(),
  // Commercial Buy/Sell units (migration 0071), set on create from the New SKU
  // dialog's canonical selectors. Free-text canonical strings (EA/LB/LF/SF/BAG/...).
  // uomStatus is NOT accepted here — it's derived server-side from the factor.
  purchaseUom: z.string().max(20).nullable().optional(),
  consumptionUom: z.string().max(20).nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type SkuCreateInput = z.infer<typeof skuCreateSchema>;

// PATCH explicitly accepts toggling isCommon and active (extends the
// partial create shape so back-office can flip those flags from the
// detail panel without sending the rest of the row).
export const skuUpdateSchema = skuCreateSchema.partial().extend({
  active: z.boolean().optional(),
});
export type SkuUpdateInput = z.infer<typeof skuUpdateSchema>;

// === Inventory adjustments ===

export const inventoryAdjustmentSchema = z.object({
  skuId: z.string().uuid(),
  delta: z.number(),
  reason: inventoryTxnReasonSchema,
  unitCostCents: z.number().int().min(0).nullable().optional(),
  jobId: z.string().uuid().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type InventoryAdjustmentInput = z.infer<typeof inventoryAdjustmentSchema>;

// Office-manager-friendly count correction. Caller submits the ACTUAL on-hand
// count they observed in the warehouse; the server computes the delta against
// the current row and writes an inventory_txns row with reason='count_correction'.
// This is a different schema from inventoryAdjustmentSchema on purpose: the
// office manager thinks in absolute counts, not deltas.
export const countCorrectionSchema = z.object({
  skuId: z.string().uuid(),
  actualOnHand: z.number().min(0),
  notes: z.string().max(500).nullable().optional(),
});
export type CountCorrectionInput = z.infer<typeof countCorrectionSchema>;

// Receive delivery — entered by the office manager (or owner) when a vendor
// drops off stock. The endpoint atomically writes an inventory_txns row with
// reason='receipt', increments skus.on_hand by quantity, and (if unitCostCents
// is provided) updates skus.cost_cents to the new cost. Owner-gated because
// it touches the cost field which back-office should not see per the
// dashboard discovery rule.
export const receiveDeliverySchema = z.object({
  skuId: z.string().uuid(),
  quantity: z.number().positive(),
  unitCostCents: z.number().int().min(0).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
});
export type ReceiveDeliveryInput = z.infer<typeof receiveDeliverySchema>;

// === Pricing engine input ===

// A pricing line is either a SKU line (existing) or a free-form MISC line (migration 0075).
export const pricingSkuLineSchema = z.object({
  kind: z.literal('sku'),
  skuId: z.string().uuid(),
  quantity: z.number().positive(),
  // Insulation batting only: the actual SF the office entered. When set,
  // the server recomputes quantity = ceil(enteredSf / coverageSf) bags and
  // uses enteredSf for labor footage. Omit for gutter / accessories.
  enteredSf: z.number().positive().optional(),
  // Shared-bag insulation presets (migration 0074): per-line SF/bag coverage +
  // R-value; the override drives SF→bags + labor when the SKU has no coverage_sf.
  coverageSfOverride: z.number().positive().optional(),
  rValue: z.string().max(20).optional(),
});

// Free-form MISC line: flat amountCents line TOTAL (sell = amountCents, cost = 0, margin 100%),
// no SKU / markup / discount. `uom` + `quantity` are INFORMATIONAL (display/PDF only).
export const pricingMiscLineSchema = z.object({
  kind: z.literal('misc'),
  description: z.string().min(1).max(500),
  amountCents: z.number().int().nonnegative(),
  uom: z.enum(CANONICAL_UOMS).optional(),
  quantity: z.number().nonnegative().optional(),
});

// Back-compat: a line with NO `kind` is treated as a SKU line, so existing all-SKU payloads
// validate byte-identically. The preprocess injects kind='sku' BEFORE discriminating.
export const pricingLineItemSchema = z.preprocess(
  (v) =>
    v && typeof v === 'object' && !Array.isArray(v) && !('kind' in (v as object))
      ? { ...(v as Record<string, unknown>), kind: 'sku' }
      : v,
  z.discriminatedUnion('kind', [pricingSkuLineSchema, pricingMiscLineSchema]),
);

export const pricingInputSchema = z.object({
  customerId: z.string().uuid(),
  lineItems: z.array(pricingLineItemSchema).min(1),
});
export type PricingInputBody = z.infer<typeof pricingInputSchema>;
export type PricingLineInput = z.infer<typeof pricingLineItemSchema>;
export type PricingSkuLineInput = z.infer<typeof pricingSkuLineSchema>;
export type PricingMiscLineInput = z.infer<typeof pricingMiscLineSchema>;

// Body for the append-a-misc-line endpoint (POST /api/quotes/:jobId/misc-lines). Separate from
// the pricing line so it can't carry SKU fields; same misc shape otherwise.
export const miscLineCreateSchema = z.object({
  description: z.string().min(1).max(500),
  amountCents: z.number().int().nonnegative(),
  uom: z.enum(CANONICAL_UOMS).optional(),
  quantity: z.number().nonnegative().optional(),
});
export type MiscLineCreateInput = z.infer<typeof miscLineCreateSchema>;

// === Quote create (dashboard-initiated quote) ===
//
// `address` is OPTIONAL because when jobId is provided we attach to an
// existing job and use its stored address — the calculator UI hides
// the address input in that mode. Required-when-creating-a-new-job
// is enforced via .superRefine() rather than the static field shape.

export const quoteCreateSchema = z
  .object({
    jobId: z.string().uuid().optional(),
    customerId: z.string().uuid(),
    address: z.string().max(500).optional(),
    displayName: z.string().min(1).max(200).optional(),
    poNumber: z.string().max(100).optional(),
    targetInstallDate: z.string().optional(),
    lineItems: z
      .array(
        z.object({
          skuId: z.string().uuid(),
          quantity: z.number().positive(),
          // Insulation batting only — see pricingInputSchema. Persisted as
          // field_estimates.entered_sf; quantity is the ceil-rounded bags.
          enteredSf: z.number().positive().optional(),
          // Shared-bag insulation presets (migration 0074): per-line coverage + R-value.
          coverageSfOverride: z.number().positive().optional(),
          rValue: z.string().max(20).optional(),
        }),
      )
      .min(1),
    /** When set, overrides the line-item-computed total for this quote.
     *  The line items are still persisted (drive inventory commitments
     *  + the PDF BoM section), but quote_amount_cents on the job row
     *  uses this value instead of the engine's computed total. */
    overrideQuoteAmountCents: z.number().int().nonnegative().nullable().optional(),
  })
  .superRefine((val, ctx) => {
    // When creating a new job (no jobId), an address is required to
    // populate the new job row. When attaching to an existing job, the
    // job's own address is used — caller doesn't need to send one.
    if (!val.jobId) {
      const trimmed = (val.address ?? '').trim();
      if (trimmed.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['address'],
          message: 'address is required when creating a new job (no jobId)',
        });
      }
    }
  });
export type QuoteCreateInput = z.infer<typeof quoteCreateSchema>;

// === Crew ===

export const crewCreateSchema = z.object({
  name: z.string().min(1).max(100),
  color: z.string().max(20).optional(),
  displayOrder: z.number().int().min(0).optional(),
});
export type CrewCreateInput = z.infer<typeof crewCreateSchema>;

export const crewUpdateSchema = crewCreateSchema.partial().extend({
  active: z.boolean().optional(),
});
export type CrewUpdateInput = z.infer<typeof crewUpdateSchema>;

// === Job Assignment ===

const dateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

export const jobAssignmentCreateSchema = z
  .object({
    jobId: z.string().uuid(),
    /** Phase 2.4: the people assigned to this row (>= 1). assignment_people
     *  is authoritative; the legacy crewId field + ja.crew_id are gone. */
    personIds: z.array(z.string().uuid()).min(1),
    /** Inclusive start day. For single-day work, set startDate === endDate. */
    startDate: dateStringSchema,
    /** Inclusive end day. Must be on or after startDate. Estimate-purpose
     *  assignments must be single-day (server enforces start === end). */
    endDate: dateStringSchema,
    /** Omit for 'install' (server default).
     *  'estimate' schedules a pre-install visit and drives /estimate
     *  Telegram visibility for the assigned people. */
    purpose: z.enum(['install', 'estimate']).optional(),
    notes: z.string().max(500).nullable().optional(),
  })
  .refine((d) => d.startDate <= d.endDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });
export type JobAssignmentCreateInput = z.infer<typeof jobAssignmentCreateSchema>;

/** PATCH input — every field optional. Server enforces:
 *   - startDate + endDate arrive together (or neither)
 *   - endDate >= startDate
 *   - estimate-purpose rows stay single-day */
export const jobAssignmentUpdateSchema = z
  .object({
    /** Phase 2.4: replace the row's people set (insert/delete diff server-side). */
    personIds: z.array(z.string().uuid()).min(1).optional(),
    startDate: dateStringSchema.optional(),
    endDate: dateStringSchema.optional(),
    notes: z.string().max(500).nullable().optional(),
  })
  .refine(
    (d) => (d.startDate === undefined) === (d.endDate === undefined),
    { message: 'startDate and endDate must be provided together', path: ['endDate'] },
  )
  .refine(
    (d) => !d.startDate || !d.endDate || d.startDate <= d.endDate,
    { message: 'endDate must be on or after startDate', path: ['endDate'] },
  );
export type JobAssignmentUpdateInput = z.infer<typeof jobAssignmentUpdateSchema>;
